import {
  ChevronDown,
  ChevronUp,
  CreateList,
  DotsVertical,
  Download,
  Lock,
  LockOpen,
  Pencil,
  Search,
  Trash,
  Users,
  World,
} from '@saleshandy/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Avatar } from '@saleshandy/design-system';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import SearchInput from '../../../../../shared/design-system/components/atoms/search-input';
import { IconPlace } from '../../../../../shared/design-system/components/input/enums';
import { accessibleOnClick } from '../../../../../shared/utils';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import ConfirmationModalV3 from '../../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { ConfirmationModalIconType } from '../../../../../shared/design-system/components/atoms/confirmation-modal/enum';
import { SavedListData } from '../v2-saved-tab';
import CreateEditSavedListModal from './create-edit-saved-list-modal';
import { Team as TeamType } from '../../../../settings/types/users-and-teams';
import { RequestStatus } from '../../../../../shared/enums/request-status';
import {
  LeadFinderListVisibility,
  FilterComponentType,
  LeadFinderPrimaryTab,
} from '../../../types';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { formatNumberToKM } from '../../../../sequence/helpers/helper';

interface SavedListProps {
  savedListsData?: SavedListData[];
  onEditList?: (listId: number, listName: string) => void;
  onExportList?: (listId: number, listName: string) => void;
  onDeleteList?: (listId: number) => Promise<void>;
  onCreateList?: (
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => Promise<void>;
  onUpdateList?: (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => Promise<void>;
  teamsList?: TeamType[];
  getTeamsListRequestStatus?: RequestStatus;
  sendGetTeamsListRequest?: VoidFunction;
  isUpdateListLoading?: boolean;
  allListsData?: SavedListData[];
  selectedListIds?: number[];
  onSelectionChange?: (selectedListIds: number[]) => void;
  handleQuery?: (params: {
    filterKey: string;
    filterValue: any;
    filterType: string;
    filterExcludeKey?: string;
  }) => void;
  pendingFilters?: URLSearchParams;
  activeLeadType?: LeadFinderPrimaryTab;
}

const SavedList: React.FC<SavedListProps> = ({
  savedListsData = [],
  onEditList,
  onExportList,
  onDeleteList,
  onCreateList,
  onUpdateList,
  teamsList = [],
  getTeamsListRequestStatus,
  sendGetTeamsListRequest = () => {},
  isUpdateListLoading = false,
  selectedListIds: controlledSelectedListIds,
  onSelectionChange,
  handleQuery,
  pendingFilters,
  activeLeadType = LeadFinderPrimaryTab.PEOPLE,
  allListsData,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [expandedSections, setExpandedSections] = useState<{
    public: boolean;
    private: boolean;
    team: boolean;
  }>({
    public: true,
    private: true,
    team: true,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [
    selectedListForEdit,
    setSelectedListForEdit,
  ] = useState<SavedListData | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [
    selectedListForDelete,
    setSelectedListForDelete,
  ] = useState<SavedListData | null>(null);
  const [hoveredListId, setHoveredListId] = useState<number | null>(null);
  const [openActionsListId, setOpenActionsListId] = useState<number | null>(
    null,
  );
  const [internalSelectedListIds, setInternalSelectedListIds] = useState<
    number[]
  >([]);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Read selected list IDs from URL if pendingFilters is provided
  const urlSelectedListIds = useMemo(() => {
    if (pendingFilters) {
      const listKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'people_list'
          : 'company_list';
      const listIncludes = pendingFilters.getAll(listKey);
      return listIncludes
        .flatMap((value) =>
          value
            .split(',')
            .map((v) => v.replace(/"/g, '').trim())
            .filter(Boolean),
        )
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id) && id > 0);
    }
    return [];
  }, [pendingFilters, activeLeadType]);

  useEffect(() => {
    if (controlledSelectedListIds !== undefined) return;
    if (!pendingFilters) return;
    setInternalSelectedListIds(urlSelectedListIds);
  }, [controlledSelectedListIds, pendingFilters, urlSelectedListIds]);

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  const selectedListIds = controlledSelectedListIds ?? internalSelectedListIds;

  const handleSelectionChange = (newSelectedIds: number[]) => {
    if (controlledSelectedListIds === undefined) {
      setInternalSelectedListIds(newSelectedIds);
    }
    onSelectionChange?.(newSelectedIds);
  };

  const handleClearAllSelectedLists = () => {
    handleSelectionChange([]);

    // Also clear search value when clearing all selected lists
    setSearchValue('');

    if (handleQuery) {
      const filterKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'people_list'
          : 'company_list';
      const filterExcludeKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'excludePeople_list'
          : 'excludeCompany_list';
      handleQuery({
        filterKey,
        filterExcludeKey,
        filterType: FilterComponentType.COMPANY_LIST,
        filterValue: {
          includedKeys: [],
          excludedKeys: [],
        },
      });
    }
  };

  const handleCheckboxChange = (
    listId: number,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation();
    const newSelectedIds = checked
      ? [...selectedListIds, listId]
      : selectedListIds.filter((id) => id !== listId);

    // Update internal/callback state
    handleSelectionChange(newSelectedIds);

    // Update URL if handleQuery is provided
    if (handleQuery) {
      const filterKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'people_list'
          : 'company_list';
      const filterExcludeKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'excludePeople_list'
          : 'excludeCompany_list';
      handleQuery({
        filterKey,
        filterExcludeKey,
        filterType: FilterComponentType.COMPANY_LIST,
        filterValue: {
          includedKeys: newSelectedIds.map(String),
          excludedKeys: [],
        },
      });
    }
  };

  // Toggle selection when clicking on the list row (name/row area)
  const handleItemClick = (listId: number, event?: React.MouseEvent) => {
    // Prevent parent handlers from running
    event?.stopPropagation();

    const isSelected = selectedListIds.includes(listId);
    const newSelectedIds = isSelected
      ? selectedListIds.filter((id) => id !== listId)
      : [...selectedListIds, listId];

    // Update internal/callback state
    handleSelectionChange(newSelectedIds);

    // Update URL if handleQuery is provided (same payload as checkbox)
    if (handleQuery) {
      const filterKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'people_list'
          : 'company_list';
      const filterExcludeKey =
        activeLeadType === LeadFinderPrimaryTab.PEOPLE
          ? 'excludePeople_list'
          : 'excludeCompany_list';
      handleQuery({
        filterKey,
        filterExcludeKey,
        filterType: FilterComponentType.COMPANY_LIST,
        filterValue: {
          includedKeys: newSelectedIds.map(String),
          excludedKeys: [],
        },
      });
    }
  };

  const handleSearch = () => {
    // Handle search logic here
    console.log('Searching for:', searchValue);
  };

  const toggleSection = (section: 'public' | 'private' | 'team') => {
    setExpandedSections((prev) => {
      const isExpanding = !prev[section];
      if (isExpanding && section === 'team' && teamsList.length === 0) {
        sendGetTeamsListRequest();
      }
      return { ...prev, [section]: isExpanding };
    });
  };

  // Group lists by visibility
  const groupedLists = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const matchesQuery = (label: string) =>
      query.length === 0 ? true : label.toLowerCase().includes(query);

    const publicLists = savedListsData.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Public &&
        matchesQuery(list.label) &&
        !list.isDefault,
    );
    const privateLists = savedListsData.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Private &&
        matchesQuery(list.label) &&
        !list.isDefault,
    );
    const teamLists = savedListsData.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Team &&
        matchesQuery(list.label) &&
        !list.isDefault,
    );
    return { public: publicLists, private: privateLists, team: teamLists };
  }, [savedListsData, searchValue]);

  const onAction = (key: string, listId: number, listName: string) => {
    switch (key) {
      case 'edit': {
        const listToEdit = savedListsData.find((list) => list.id === listId);
        if (listToEdit) {
          setSelectedListForEdit(listToEdit);
          setShowEditModal(true);
        }
        onEditList?.(listId, listName);
        break;
      }
      case 'export':
        onExportList?.(listId, listName);
        break;
      case 'delete': {
        const listToDelete = savedListsData.find((list) => list.id === listId);
        if (listToDelete) {
          setSelectedListForDelete(listToDelete);
          setShowDeleteModal(true);
        }
        break;
      }
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedListForDelete) return;
    try {
      await onDeleteList?.(selectedListForDelete.id);
      setShowDeleteModal(false);
      setSelectedListForDelete(null);
    } catch (error) {
      console.error('Failed to delete list', error);
    }
  };

  const handleUpdateList = async (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => {
    try {
      if (listId === null && onCreateList) {
        await onCreateList(listName, isShared, teamIds);
      } else {
        await onUpdateList?.(listId, listName, isShared, teamIds);
      }
      setShowEditModal(false);
      setSelectedListForEdit(null);
      setIsCreateMode(false);
    } catch (error) {
      console.error(
        listId === null ? 'Failed to create list' : 'Failed to update list',
        error,
      );
    }
  };

  const handleCreateList = () => {
    setSelectedListForEdit(null);
    setIsCreateMode(true);
    setShowEditModal(true);
  };

  const handleToggleSearch = () => {
    setShowSearchInput((prev) => !prev);
    if (showSearchInput) {
      // Clear search when closing
      setSearchValue('');
    }
  };

  const renderListActions = (
    listId: number,
    listName: string,
    isDefault: boolean,
  ) => (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <Dropdown
        bsPrefix="dropdown-custom"
        drop="down"
        show={openActionsListId === listId}
        onToggle={(nextShow) => {
          setOpenActionsListId(nextShow ? listId : null);
        }}
      >
        <Dropdown.Toggle
          bsPrefix="dropdown-toggle-custom"
          variant="dropdown-custom"
          id={`dropdown-list-${listId}`}
          className={openActionsListId === listId ? 'active' : ''}
        >
          <DotsVertical width={14} height={14} color="#6B7280" />
        </Dropdown.Toggle>
        <Dropdown.Menu bsPrefix="dropdown-menu-custom">
          {!isDefault && (
            <Dropdown.Item
              bsPrefix="dropdown-item-custom"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onAction('edit', listId, listName);
              }}
            >
              <Pencil width={16} height={16} className="gray-txt-15" />
              <span className="regular-2 blue-txt-16">Edit</span>
            </Dropdown.Item>
          )}

          <Dropdown.Item
            bsPrefix="dropdown-item-custom"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onAction('export', listId, listName);
            }}
          >
            <Download width={16} height={16} className="gray-txt-15" />
            <span className="regular-2 blue-txt-16">Export</span>
          </Dropdown.Item>
          {!isDefault && (
            <Dropdown.Item
              bsPrefix="dropdown-item-custom"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onAction('delete', listId, listName);
              }}
            >
              <Trash width={16} height={16} className="gray-txt-15" />
              <span className="regular-2 blue-txt-16">Delete</span>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  return (
    <>
      <ConfirmationModalV3
        show={showDeleteModal}
        iconType={ConfirmationModalIconType.TRASH_RED}
        title="Are you sure you want to delete this list ?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedListForDelete(null);
        }}
        onSubmit={handleConfirmDelete}
        cancelButtonText="Cancel"
        submitButtonText="Delete"
        submitButtonClassName="saved-list-delete-modal__submit-btn"
        cancelButtonClassName="saved-list-delete-modal__cancel-btn"
        className="saved-list-delete-modal"
        contents={[
          'Deleting this list will not remove any saved records.',
          'All records will remain in the “All Companies” default list.',
          'Only the list will be removed.',
        ]}
        showCloseIcon={false}
      />
      <CreateEditSavedListModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedListForEdit(null);
          setIsCreateMode(false);
        }}
        onSave={handleUpdateList}
        isLoading={isUpdateListLoading}
        teamsList={teamsList}
        listDetails={
          selectedListForEdit
            ? {
                listId: selectedListForEdit.id,
                name: selectedListForEdit.label,
                visibility: selectedListForEdit.visibility,
                teams: [], // You may need to add teams to SavedListData type
              }
            : undefined
        }
        getTeamsListRequestStatus={getTeamsListRequestStatus}
        sendGetTeamsListRequest={sendGetTeamsListRequest}
        isCreateMode={isCreateMode}
        existingLists={allListsData}
        primaryButtonLabel={isUpdateListLoading ? 'Saving...' : 'Create'}
      />
      <div className="saved-list">
        <div className="saved-list__header">
          <div className="d-flex align-items-center gap-8">
            <h3 className="saved-list__header-title">Lists</h3>
            {selectedListIds?.length > 0 && (
              <span className="saved-list__header-count">
                {selectedListIds?.length}
              </span>
            )}
          </div>
          <div className="saved-list__header-actions">
            {selectedListIds?.length > 0 && (
              <div className="d-flex align-items-center gap-8">
                <span
                  className="saved-list__header-actions-clear"
                  {...accessibleOnClick(handleClearAllSelectedLists)}
                >
                  Clear all
                </span>
                <div className="saved-list__header-actions-divider" />
              </div>
            )}
            <OverlayTooltip text="Create List" placement={Placement.Bottom}>
              <div
                className="saved-list__header-create"
                {...accessibleOnClick(handleCreateList)}
              >
                <CreateList width={16} height={16} />
              </div>
            </OverlayTooltip>
            <OverlayTooltip text="Search List" placement={Placement.Bottom}>
              <div
                className="saved-list__header-search"
                role="button"
                tabIndex={0}
                {...accessibleOnClick(handleToggleSearch)}
              >
                <Search width={16} height={16} />
              </div>
            </OverlayTooltip>
          </div>
        </div>
        {showSearchInput && (
          <div className="saved-list__search">
            <SearchInput
              placeholder="Search Lists"
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              onSearch={handleSearch}
              searchIconPlace={IconPlace.Left}
              className="search-input"
              inputRef={searchInputRef}
            />
          </div>
        )}
        {/* <div className="saved-list__companies">
          <Building width={16} height={16} color="#1F2937" />
          <span className="saved-list__companies-text">
            {activeLeadType === LeadFinderPrimaryTab.PEOPLE
              ? 'All People'
              : 'All Companies'}
          </span>
        </div>
        <div
          className="divider"
          style={{
            marginLeft: '12px',
            marginRight: '12px',
            width: 'calc(100% - 24px)',
          }}
        /> */}
        <div className="saved-list__accordion">
          {/* Public Lists */}
          <div className="saved-list__accordion-item">
            <div
              className="saved-list__accordion-header"
              {...(groupedLists.public.length > 0
                ? accessibleOnClick(() => toggleSection('public'))
                : {})}
            >
              <div className="saved-list__accordion-header-left">
                <World width={16} height={16} color="#1f2937" />
                <span className="saved-list__accordion-title">
                  Public Lists
                </span>
                <span className="saved-list__accordion-badge">
                  {formatNumberToKM(groupedLists.public.length)}
                </span>
              </div>
              {groupedLists.public.length > 0 &&
                (expandedSections.public ? (
                  <ChevronUp width={14} height={14} color="#6B7280" />
                ) : (
                  <ChevronDown width={14} height={14} color="#6B7280" />
                ))}
            </div>
            {groupedLists.public.length > 0 && expandedSections.public && (
              <div className="saved-list__accordion-content">
                <div className="saved-list__accordion-list">
                  {groupedLists.public?.map((list) => (
                    <div
                      key={list.id}
                      className={`saved-list__accordion-list-item ${
                        selectedListIds.includes(list.id)
                          ? 'saved-list__accordion-list-item--selected'
                          : ''
                      }`}
                      onMouseEnter={() => setHoveredListId(list?.id)}
                      onMouseLeave={() => setHoveredListId(null)}
                      {...accessibleOnClick((e: React.MouseEvent) =>
                        handleItemClick(list.id, e),
                      )}
                    >
                      <Checkbox
                        checked={selectedListIds.includes(list.id)}
                        onChange={(checked, event) =>
                          handleCheckboxChange(list.id, checked, event)
                        }
                        className="saved-list__checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="saved-list__accordion-list-item-left">
                        <TruncatedTextWithAutoTooltip
                          text={list.label}
                          className="saved-list__accordion-list-item-text"
                        />
                        {(hoveredListId === list?.id ||
                          selectedListIds.includes(list.id)) &&
                          list.count !== undefined && (
                            <span className="saved-list__accordion-list-item-count">
                              {formatNumberToKM(list.count)}
                            </span>
                          )}
                      </div>
                      <div className="saved-list__accordion-list-item-right">
                        {(hoveredListId === list?.id ||
                          selectedListIds.includes(list.id)) && (
                          <Avatar
                            firstName={list.owner.firstName}
                            lastName={list.owner.lastName}
                          />
                        )}
                        {(hoveredListId === list?.id ||
                          openActionsListId === list?.id) &&
                          renderListActions(
                            list.id,
                            list.label,
                            list?.isDefault,
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Private Lists */}
          <div className="saved-list__accordion-item">
            <div
              className="saved-list__accordion-header"
              {...(groupedLists.private.length > 0
                ? accessibleOnClick(() => toggleSection('private'))
                : {})}
            >
              <div className="saved-list__accordion-header-left">
                <Lock width={16} height={16} color="#1f2937" />
                <span className="saved-list__accordion-title">
                  Private Lists
                </span>
                <span className="saved-list__accordion-badge">
                  {formatNumberToKM(groupedLists.private.length)}
                </span>
              </div>
              {groupedLists.private.length > 0 &&
                (expandedSections.private ? (
                  <ChevronUp width={14} height={14} color="#6B7280" />
                ) : (
                  <ChevronDown width={14} height={14} color="#6B7280" />
                ))}
            </div>
            {groupedLists.private.length > 0 && expandedSections.private && (
              <div className="saved-list__accordion-content">
                <div className="saved-list__accordion-list">
                  {groupedLists.private.map((list) => (
                    <div
                      key={list.id}
                      className={`saved-list__accordion-list-item ${
                        selectedListIds.includes(list.id)
                          ? 'saved-list__accordion-list-item--selected'
                          : ''
                      }`}
                      onMouseEnter={() => setHoveredListId(list?.id)}
                      onMouseLeave={() => setHoveredListId(null)}
                      {...accessibleOnClick((e: React.MouseEvent) =>
                        handleItemClick(list.id, e),
                      )}
                    >
                      <Checkbox
                        checked={selectedListIds.includes(list.id)}
                        onChange={(checked, event) =>
                          handleCheckboxChange(list.id, checked, event)
                        }
                        className="saved-list__checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="saved-list__accordion-list-item-left">
                        <TruncatedTextWithAutoTooltip
                          text={list.label}
                          className="saved-list__accordion-list-item-text"
                        />
                        {(hoveredListId === list?.id ||
                          selectedListIds.includes(list.id)) &&
                          list.count !== undefined && (
                            <span className="saved-list__accordion-list-item-count">
                              {formatNumberToKM(list.count)}
                            </span>
                          )}
                      </div>
                      <div className="saved-list__accordion-list-item-right">
                        {hoveredListId === list?.id &&
                          renderListActions(
                            list.id,
                            list.label,
                            list?.isDefault,
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Teams Lists */}
          <div className="saved-list__accordion-item">
            <div
              className="saved-list__accordion-header"
              {...(groupedLists.team.length > 0
                ? accessibleOnClick(() => toggleSection('team'))
                : {})}
            >
              <div className="saved-list__accordion-header-left">
                <Users width={16} height={16} color="#1f2937" />
                <span className="saved-list__accordion-title">Teams</span>
                <span className="saved-list__accordion-badge">
                  {formatNumberToKM(groupedLists.team.length)}
                </span>
              </div>
              {groupedLists.team.length > 0 &&
                (expandedSections.team ? (
                  <ChevronUp width={14} height={14} color="#6B7280" />
                ) : (
                  <ChevronDown width={14} height={14} color="#6B7280" />
                ))}
            </div>
            {groupedLists.team.length > 0 && expandedSections.team && (
              <div className="saved-list__accordion-content">
                <div className="saved-list__accordion-list">
                  {groupedLists.team.map((list) => (
                    <div
                      key={list.id}
                      className={`saved-list__accordion-list-item ${
                        selectedListIds.includes(list.id)
                          ? 'saved-list__accordion-list-item--selected'
                          : ''
                      }`}
                      onMouseEnter={() => setHoveredListId(list?.id)}
                      onMouseLeave={() => setHoveredListId(null)}
                      {...accessibleOnClick((e: React.MouseEvent) =>
                        handleItemClick(list.id, e),
                      )}
                    >
                      <Checkbox
                        checked={selectedListIds.includes(list.id)}
                        onChange={(checked, event) =>
                          handleCheckboxChange(list.id, checked, event)
                        }
                        className="saved-list__checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="saved-list__accordion-list-item-left">
                        <TruncatedTextWithAutoTooltip
                          text={list.label}
                          className="saved-list__accordion-list-item-text"
                        />
                        {list.count !== undefined && (
                          <span className="saved-list__accordion-list-item-count">
                            {formatNumberToKM(list.count)}
                          </span>
                        )}
                      </div>
                      <div className="saved-list__accordion-list-item-right">
                        {(hoveredListId === list?.id ||
                          selectedListIds.includes(list.id)) && (
                          <Avatar
                            firstName={list.owner.firstName}
                            lastName={list.owner.lastName}
                          />
                        )}
                        {renderListActions(
                          list.id,
                          list.label,
                          list?.isDefault,
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SavedList;
